import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="admin--phrases"
export default class extends Controller {
  static targets = [
    "searchInput", "categoryFilter", "fileList", "fileItem",
    "yamlEditor", "structureEditor", "tabButton", "tabContent",
    "phraseInput", "bulkEditModal", "bulkEditForm", "selectedPhrases",
    "selectAllCheckbox", "phraseCheckbox", "languageSelector"
  ]

  declare readonly searchInputTarget: HTMLInputElement
  declare readonly categoryFilterTarget: HTMLSelectElement
  declare readonly fileListTarget: HTMLElement
  declare readonly fileItemTargets: HTMLElement[]
  declare readonly yamlEditorTarget: HTMLTextAreaElement
  declare readonly structureEditorTarget: HTMLElement
  declare readonly tabButtonTargets: HTMLElement[]
  declare readonly tabContentTargets: HTMLElement[]
  declare readonly phraseInputTargets: HTMLInputElement[]
  declare readonly bulkEditModalTarget: HTMLElement
  declare readonly bulkEditFormTarget: HTMLFormElement
  declare readonly selectedPhrasesTarget: HTMLElement
  declare readonly selectAllCheckboxTarget: HTMLInputElement
  declare readonly phraseCheckboxTargets: HTMLInputElement[]
  declare readonly languageSelectorTarget: HTMLSelectElement

  private selectedPhrases: Set<string> = new Set()

  connect() {
    this.initializeFilters()
    this.initializeTabs()
  }

  // File browsing and filtering
  initializeFilters() {
    this.searchInputTarget?.addEventListener('input', this.debounce(this.filterFiles.bind(this), 300))
    this.categoryFilterTarget?.addEventListener('change', this.filterFiles.bind(this))
  }

  filterFiles() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    const selectedCategory = this.categoryFilterTarget.value

    this.fileItemTargets.forEach(item => {
      const fileName = item.dataset.fileName?.toLowerCase() || ''
      const fileCategory = item.dataset.fileCategory || ''
      const filePath = item.dataset.filePath?.toLowerCase() || ''

      const matchesSearch = !searchTerm ||
        fileName.includes(searchTerm) ||
        filePath.includes(searchTerm)

      const matchesCategory = !selectedCategory ||
        selectedCategory === 'all' ||
        fileCategory === selectedCategory

      item.style.display = matchesSearch && matchesCategory ? 'block' : 'none'
    })
  }

  debounce(func: Function, wait: number) {
    let timeout: number | NodeJS.Timeout
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Tab switching for file view
  initializeTabs() {
    if (this.tabButtonTargets.length > 0) {
      this.switchToTab(this.tabButtonTargets[0])
    }
  }

  switchTab(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    this.switchToTab(button)
  }

  private switchToTab(activeButton: HTMLElement) {
    if (!activeButton) return

    const targetTab = activeButton.dataset.tab

    // Update tab buttons
    this.tabButtonTargets.forEach(btn => {
      const isActive = btn.dataset.tab === targetTab
      btn.classList.toggle('bg-violet-600', isActive)
      btn.classList.toggle('text-white', isActive)
      btn.classList.toggle('bg-gray-200', !isActive)
      btn.classList.toggle('text-gray-700', !isActive)
    })

    // Update tab content
    this.tabContentTargets.forEach(content => {
      content.classList.toggle('hidden', content.dataset.tab !== targetTab)
    })
  }

  // Individual phrase editing
  editPhrase(event: Event) {
    const button = event.currentTarget as HTMLElement
    const phrasePath = button.dataset.phrasePath
    const currentValue = button.dataset.currentValue
    const input = button.previousElementSibling as HTMLInputElement

    if (input.style.display === 'none') {
      // Switch to edit mode
      input.style.display = 'block'
      input.focus()
      input.value = currentValue || ''
      button.textContent = 'Save'
      button.classList.remove('bg-blue-500', 'hover:bg-blue-600')
      button.classList.add('bg-green-500', 'hover:bg-green-600')
    } else {
      // Save the change
      this.savePhrase(phrasePath!, input.value)
      input.style.display = 'none'
      button.textContent = 'Edit'
      button.classList.remove('bg-green-500', 'hover:bg-green-600')
      button.classList.add('bg-blue-500', 'hover:bg-blue-600')
    }
  }

  async savePhrase(phrasePath: string, newValue: string) {
    const filePath = this.element.dataset.filePath
    const locale = this.languageSelectorTarget?.value || null

    try {
      const response = await fetch('/admin/phrases/update_phrase', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          file_path: filePath,
          key_path: phrasePath,
          value: newValue,
          locale: locale
        })
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification('Phrase updated successfully!', 'success')
        // Update the display value
        const button = this.element.querySelector(`[data-phrase-path="${phrasePath}"]`) as HTMLElement
        if (button) {
          const displaySpan = button.nextElementSibling as HTMLElement
          if (displaySpan) {
            displaySpan.textContent = this.truncateValue(newValue)
          }
          button.dataset.currentValue = newValue
        }
      } else {
        this.showNotification('Failed to update phrase', 'error')
      }
    } catch (error) {
      this.showNotification('Error updating phrase', 'error')
    }
  }

  // Bulk editing functionality
  togglePhraseSelection(event: Event) {
    const checkbox = event.currentTarget as HTMLInputElement
    const phrasePath = checkbox.dataset.phrasePath

    if (checkbox.checked) {
      this.selectedPhrases.add(phrasePath!)
    } else {
      this.selectedPhrases.delete(phrasePath!)
    }

    this.updateBulkEditUI()
  }

  toggleSelectAll(event: Event) {
    const checkbox = event.currentTarget as HTMLInputElement
    const isChecked = checkbox.checked

    this.phraseCheckboxTargets.forEach(cb => {
      cb.checked = isChecked
      const phrasePath = cb.dataset.phrasePath
      if (isChecked) {
        this.selectedPhrases.add(phrasePath!)
      } else {
        this.selectedPhrases.delete(phrasePath!)
      }
    })

    this.updateBulkEditUI()
  }

  updateBulkEditUI() {
    const count = this.selectedPhrases.size
    this.selectedPhrasesTarget.textContent = `${count} phrases selected`

    if (count > 0) {
      this.bulkEditModalTarget.classList.remove('hidden')
    } else {
      this.bulkEditModalTarget.classList.add('hidden')
    }
  }

  openBulkEditModal() {
    if (this.selectedPhrases.size === 0) return

    // Populate the bulk edit form
    const form = this.bulkEditFormTarget
    form.innerHTML = ''

    this.selectedPhrases.forEach(phrasePath => {
      const div = document.createElement('div')
      div.className = 'mb-4 p-4 bg-gray-50 rounded-lg'
      div.innerHTML = `
        <label class="block text-sm font-medium text-gray-700 mb-2">${phrasePath}</label>
        <input type="text" name="updates[][key_path]" value="${phrasePath}" class="hidden">
        <input type="text" name="updates[][value]" placeholder="New value" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-violet-500 focus:border-violet-500">
        <input type="text" name="updates[][file_path]" value="${this.element.dataset.filePath}" class="hidden">
        <input type="text" name="updates[][locale]" value="${this.languageSelectorTarget?.value || ''}" class="hidden">
      `
      form.appendChild(div)
    })

    this.bulkEditModalTarget.classList.remove('hidden')
  }

  closeBulkEditModal() {
    this.bulkEditModalTarget.classList.add('hidden')
  }

  async submitBulkEdit(event: Event) {
    event.preventDefault()

    const formData = new FormData(this.bulkEditFormTarget)
    const updates = []

    // Group form data by index
    const dataByIndex: { [key: number]: any } = {}
    for (const [key, value] of formData.entries()) {
      const match = key.match(/updates\[(\d+)\]\[(\w+)\]/)
      if (match) {
        const index = parseInt(match[1])
        const field = match[2]
        dataByIndex[index] = dataByIndex[index] || {}
        dataByIndex[index][field] = value
      }
    }

    Object.values(dataByIndex).forEach((update: any) => {
      updates.push(update)
    })

    try {
      const response = await fetch('/admin/phrases/bulk_update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({ updates })
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification(result.message, 'success')
        this.closeBulkEditModal()
        this.clearSelections()
        // Reload the page to show updated content
        window.location.reload()
      } else {
        this.showNotification('Bulk update failed', 'error')
      }
    } catch (error) {
      this.showNotification('Error during bulk update', 'error')
    }
  }

  clearSelections() {
    this.selectedPhrases.clear()
    this.phraseCheckboxTargets.forEach(cb => cb.checked = false)
    this.selectAllCheckboxTarget.checked = false
    this.updateBulkEditUI()
  }

  // YAML editing
  updateYamlEditor() {
    // This would be called when switching to YAML mode
    // For now, just ensure the textarea has the current content
  }

  async saveYamlChanges() {
    const yamlContent = this.yamlEditorTarget.value
    const filePath = this.element.dataset.filePath

    try {
      const response = await fetch('/admin/phrases/update_yaml', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          file_path: filePath,
          yaml_content: yamlContent
        })
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification('YAML updated successfully!', 'success')
      } else {
        this.showNotification('Failed to update YAML', 'error')
      }
    } catch (error) {
      this.showNotification('Error updating YAML', 'error')
    }
  }

  // Utility methods
  private getCsrfToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    return token?.content || ''
  }

  private truncateValue(value: string): string {
    return value.length > 50 ? `${value.substring(0, 47)}...` : value
  }

  private showNotification(message: string, type: 'success' | 'error' = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg animate-fade-in-up ${
      type === 'success'
        ? 'bg-green-50 border border-green-200 text-green-700'
        : 'bg-red-50 border border-red-200 text-red-700'
    }`

    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
          ${type === 'success'
            ? '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>'
            : '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>'
          }
        </svg>
        <p class="font-medium">${message}</p>
      </div>
    `

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('animate-fade-out')
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }
}